import type { ReactNode } from "react";
import { cn } from "@/lib/utils/cn";

interface StatTileProps {
  label: string;
  value: ReactNode;
  hint?: string;
  icon?: ReactNode;
  trend?: { value: string; positive: boolean };
  /** Highlights this tile as the one number in the row that matters most
   * (e.g. "available balance") - a tinted primary surface instead of the
   * plain neutral card the rest of the row uses. */
  emphasis?: boolean;
  /** Makes the whole tile a button (keyboard + pointer). */
  onClick?: () => void;
  /** Accessible name for the clickable tile. */
  ariaLabel?: string;
  /** Marks the tile as the currently applied filter. */
  active?: boolean;
  className?: string;
}

export function StatTile({ label, value, hint, icon, trend, emphasis, onClick, ariaLabel, active, className }: StatTileProps) {
  const Root = onClick ? "button" : "div";
  return (
    <Root
      {...(onClick ? { type: "button" as const, onClick, "aria-label": ariaLabel, "aria-pressed": !!active } : {})}
      className={cn(
        // Every StatTile is, by definition, a KPI/stat surface - the
        // premium hover lift+glow (2026-08-18 interaction layer, strengthened
        // v2) is on by default here rather than opt-in, the same way Card's
        // `interactive` prop must be explicitly requested for its more
        // general, not-always-a-KPI usages. `group` drives the icon-chip
        // tint below.
        "group rounded-xl2 border p-4 shadow-soft transition-all duration-200 ease-out hover:-translate-y-1 hover:shadow-hover active:translate-y-0 active:shadow-soft active:duration-100",
        emphasis ? "border-primary-200 bg-primary-50 hover:border-primary-300" : "border-slate-200 bg-white hover:border-primary-200",
        onClick && "w-full cursor-pointer text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2",
        active && "border-primary-400 ring-1 ring-primary-300",
        className,
      )}
    >
      {(icon || trend) && (
        <div className="flex items-center justify-between">
          {icon && (
            <div className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors duration-200 ease-out group-hover:bg-primary-50 group-hover:text-primary-600">
              {icon}
            </div>
          )}
          {trend && (
            <span
              className={cn(
                "inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold",
                trend.positive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700",
              )}
            >
              {trend.positive ? "▲" : "▼"} {trend.value}
            </span>
          )}
        </div>
      )}
      <p
        className={cn(
          "text-2xl font-semibold tabular-nums",
          (icon || trend) && "mt-3",
          emphasis ? "text-primary-900" : "text-slate-900",
        )}
      >
        {value}
      </p>
      <div className="mt-0.5 flex flex-wrap items-baseline gap-x-1.5">
        <p className={cn("text-xs font-medium", emphasis ? "text-primary-700" : "text-slate-500")}>{label}</p>
        {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
      </div>
    </Root>
  );
}

/** Consistent grid wrapper for a row of StatTiles - callers own the column
 * counts (they vary: 3 tiles vs. 5), this just standardizes the spacing so
 * every module's "stats above the table" row shares the same rhythm. */
export function StatTileRow({ children, className }: { children: ReactNode; className: string }) {
  return <div className={cn("mb-4 grid gap-3", className)}>{children}</div>;
}
